/**
 * React Query hooks for Calendars API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import calendarsService from '@/api/services/calendars.service';
import type {
  CalendarCreateRequest,
  CalendarUpdateRequest,
  ResourceCreateRequest,
  ResourceUpdateRequest,
  ResourceAvailabilityRequest,
  EventCreateRequest,
  EventUpdateRequest,
  CheckOverlapRequest,
  UserOptionRequest,
  ResourcesTableParams,
  PaginationParams,
  ResourceListParams,
  SunTimesParams,
} from '@/api/types/calendars.types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const calendarsKeys = {
  all: ['calendars'] as const,
  lists: () => [...calendarsKeys.all, 'list'] as const,
  list: (params?: PaginationParams) =>
    [...calendarsKeys.lists(), params] as const,
  details: () => [...calendarsKeys.all, 'detail'] as const,
  detail: (id: string) => [...calendarsKeys.details(), id] as const,

  resources: {
    all: ['calendars', 'resources'] as const,
    lists: () => [...calendarsKeys.resources.all, 'list'] as const,
    list: (params?: ResourceListParams) =>
      [...calendarsKeys.resources.lists(), params] as const,
    table: (params?: ResourcesTableParams) =>
      [...calendarsKeys.resources.all, 'table', params] as const,
    details: () => [...calendarsKeys.resources.all, 'detail'] as const,
    detail: (id: string) => [...calendarsKeys.resources.details(), id] as const,
  },

  events: {
    all: ['calendars', 'events'] as const,
    lists: () => [...calendarsKeys.events.all, 'list'] as const,
    list: (calendarId: string, params?: any) =>
      [...calendarsKeys.events.lists(), calendarId, params] as const,
    details: () => [...calendarsKeys.events.all, 'detail'] as const,
    detail: (id: string) => [...calendarsKeys.events.details(), id] as const,
  },

  options: {
    all: ['calendars', 'options'] as const,
    detail: (key: string) => [...calendarsKeys.options.all, key] as const,
  },

  sunTimes: {
    all: ['calendars', 'sunTimes'] as const,
    detail: (airportId: string, params: SunTimesParams) =>
      [...calendarsKeys.sunTimes.all, airportId, params] as const,
  },

  weather: {
    all: ['calendars', 'weather'] as const,
    detail: (latitude: number, longitude: number) =>
      [...calendarsKeys.weather.all, latitude, longitude] as const,
  },

  yearlySummary: {
    all: ['calendars', 'yearlySummary'] as const,
    detail: (calendarId: string, year: number, resourceIds?: string[]) =>
      [
        ...calendarsKeys.yearlySummary.all,
        calendarId,
        year,
        resourceIds,
      ] as const,
  },
};

// ============================================================================
// CALENDAR HOOKS
// ============================================================================

/**
 * Get all calendars
 */
export const useCalendars = (params?: PaginationParams) => {
  return useQuery({
    queryKey: calendarsKeys.list(params),
    queryFn: () => calendarsService.getCalendars(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get calendar by ID
 */
export const useCalendarById = (calendarId: string) => {
  return useQuery({
    queryKey: calendarsKeys.detail(calendarId),
    queryFn: () => calendarsService.getCalendarById(calendarId),
    enabled: !!calendarId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Create a new calendar
 */
export const useCreateCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CalendarCreateRequest) =>
      calendarsService.createCalendar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarsKeys.lists() });
      message.success('Calendar created successfully');
    },
    onError: () => {
      message.error('Failed to create calendar');
    },
  });
};

/**
 * Update a calendar
 */
export const useUpdateCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      calendarId,
      data,
    }: {
      calendarId: string;
      data: CalendarUpdateRequest;
    }) => calendarsService.updateCalendar(calendarId, data),
    onSuccess: (_, { calendarId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.detail(calendarId),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.lists() });
      message.success('Calendar updated successfully');
    },
    onError: () => {
      message.error('Failed to update calendar');
    },
  });
};

/**
 * Delete a calendar
 */
export const useDeleteCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (calendarId: string) =>
      calendarsService.deleteCalendar(calendarId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarsKeys.lists() });
      message.success('Calendar deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete calendar');
    },
  });
};

// ============================================================================
// RESOURCE HOOKS
// ============================================================================

/**
 * Get all resources with optional filtering
 */
export const useResources = (params?: ResourceListParams) => {
  return useQuery({
    queryKey: calendarsKeys.resources.list(params),
    queryFn: () => calendarsService.getResources(params),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get resource by ID
 */
export const useResourceById = (resourceId: string) => {
  return useQuery({
    queryKey: calendarsKeys.resources.detail(resourceId),
    queryFn: () => calendarsService.getResourceById(resourceId),
    enabled: !!resourceId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get resources table (UDC format)
 */
export const useResourcesTable = (params?: ResourcesTableParams) => {
  return useQuery({
    queryKey: calendarsKeys.resources.table(params),
    queryFn: () => calendarsService.getResourcesTable(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Create a new resource
 */
export const useCreateResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResourceCreateRequest) =>
      calendarsService.createResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.lists(),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.resources.all });
      message.success('Resource created successfully');
    },
    onError: () => {
      message.error('Failed to create resource');
    },
  });
};

/**
 * Update a resource
 */
export const useUpdateResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resourceId,
      data,
    }: {
      resourceId: string;
      data: ResourceUpdateRequest;
    }) => calendarsService.updateResource(resourceId, data),
    onSuccess: (_, { resourceId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.detail(resourceId),
      });
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.lists(),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.resources.all });
      message.success('Resource updated successfully');
    },
    onError: () => {
      message.error('Failed to update resource');
    },
  });
};

/**
 * Toggle resource availability
 */
export const useToggleResourceAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resourceId,
      data,
    }: {
      resourceId: string;
      data: ResourceAvailabilityRequest;
    }) => calendarsService.toggleResourceAvailability(resourceId, data),
    onSuccess: (_, { resourceId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.detail(resourceId),
      });
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.lists(),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.resources.all });
      message.success('Resource availability updated');
    },
    onError: () => {
      message.error('Failed to update resource availability');
    },
  });
};

/**
 * Delete a resource
 */
export const useDeleteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resourceId: string) =>
      calendarsService.deleteResource(resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.resources.lists(),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.resources.all });
      message.success('Resource deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete resource');
    },
  });
};

// ============================================================================
// EVENT HOOKS
// ============================================================================

/**
 * Get events for a calendar
 */
export const useEvents = (
  calendarId: string,
  params?: {
    start_date?: string;
    end_date?: string;
    resource_ids?: string[];
    skip?: number;
    limit?: number;
  }
) => {
  return useQuery({
    queryKey: calendarsKeys.events.list(calendarId, params),
    queryFn: () => calendarsService.getEvents(calendarId, params),
    enabled: !!calendarId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Get event by ID
 */
export const useEventById = (eventId: string) => {
  return useQuery({
    queryKey: calendarsKeys.events.detail(eventId),
    queryFn: () => calendarsService.getEventById(eventId),
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Create a new event
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      calendarId,
      data,
    }: {
      calendarId: string;
      data: EventCreateRequest;
    }) => calendarsService.createEvent(calendarId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarsKeys.events.all });
      message.success('Event created successfully');
    },
    onError: (error: any) => {
      // Handle overlap conflicts specially
      if (error?.response?.status === 409) {
        message.error('Resource conflict detected. Please check availability.');
      } else {
        message.error('Failed to create event');
      }
    },
  });
};

/**
 * Update an event
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: EventUpdateRequest;
    }) => calendarsService.updateEvent(eventId, data),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.events.detail(eventId),
      });
      queryClient.invalidateQueries({ queryKey: calendarsKeys.events.all });
      message.success('Event updated successfully');
    },
    onError: () => {
      message.error('Failed to update event');
    },
  });
};

/**
 * Delete an event
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => calendarsService.deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarsKeys.events.all });
      message.success('Event deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete event');
    },
  });
};

// ============================================================================
// OVERLAP DETECTION
// ============================================================================

/**
 * Check for resource conflicts
 */
export const useCheckOverlap = () => {
  return useMutation({
    mutationFn: (data: CheckOverlapRequest) =>
      calendarsService.checkOverlap(data),
  });
};

// ============================================================================
// USER OPTIONS
// ============================================================================

/**
 * Get user option by key
 */
export const useUserOption = (key: string) => {
  return useQuery({
    queryKey: calendarsKeys.options.detail(key),
    queryFn: () => calendarsService.getUserOption(key),
    enabled: !!key,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // Don't retry if option doesn't exist
  });
};

/**
 * Set user option
 */
export const useSetUserOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserOptionRequest) =>
      calendarsService.setUserOption(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: calendarsKeys.options.detail(data.key),
      });
    },
  });
};

// ============================================================================
// GOLDENWINGS - SUN TIMES
// ============================================================================

/**
 * Get sun times (sunrise, sunset, twilight) for a specific airport
 * Supports both single date and date range queries
 * Used for VFR flight planning and daylight hours visualization
 */
export const useSunTimes = (
  airportId: string,
  params: SunTimesParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: calendarsKeys.sunTimes.detail(airportId, params),
    queryFn: () => calendarsService.getAirportSunTimes(airportId, params),
    enabled:
      enabled &&
      !!airportId &&
      (!!params.date || (!!params.date_from && !!params.date_to)),
    staleTime: 60 * 60 * 1000, // 1 hour (sun times don't change frequently)
  });
};

// ============================================================================
// GOLDENWINGS - WEATHER FORECAST
// ============================================================================

/**
 * Get weather forecast for a location (latitude/longitude)
 * Returns 5-day forecast from OpenWeatherMap
 * Used for flight planning and weather conditions visualization
 *
 * @param latitude - Location latitude (-90 to 90)
 * @param longitude - Location longitude (-180 to 180)
 * @param enabled - Whether to enable the query (default: true)
 * @param days - Number of days to forecast (1-5, default: 5)
 */
export const useWeatherForecast = (
  latitude: number | undefined,
  longitude: number | undefined,
  enabled: boolean = true,
  days: number = 5
) => {
  return useQuery({
    queryKey: calendarsKeys.weather.detail(latitude || 0, longitude || 0),
    queryFn: () => {
      if (!latitude || !longitude) {
        throw new Error(
          'Latitude and longitude are required for weather forecast'
        );
      }
      return calendarsService.getWeatherForecast({
        latitude,
        longitude,
        days,
      });
    },
    enabled: enabled && !!latitude && !!longitude,
    staleTime: 30 * 60 * 1000, // 30 minutes (matches backend cache)
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
    retryDelay: 1000,
  });
};

// ============================================================================
// YEARLY SUMMARY HOOK
// ============================================================================

/**
 * Hook for yearly calendar summary (optimized for performance)
 * Returns lightweight day-by-day aggregates instead of full events
 *
 * Use this for YearView to dramatically improve performance
 */
export const useYearlySummary = (
  calendarId: string,
  year: number,
  resourceIds?: string[],
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: calendarsKeys.yearlySummary.detail(calendarId, year, resourceIds),
    queryFn: () =>
      calendarsService.getYearlySummary(calendarId, year, resourceIds),
    enabled: enabled && !!calendarId && !!year,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
  });
};
